export const calculatePlayerOwed = (playerId, players, expenses, games, walletTransactions, selectedTournament = 'all') => {
    let totalOwed = 0;
    let totalPaid = 0;
    const expenseBreakdown = [];

    // Find the player object to get both id, userId, and email
    const player = players.find(p => p.id === playerId || p.userId === playerId);
    const playerIds = player ? [player.id, player.userId].filter(Boolean) : [playerId];
    const playerEmail = player?.email?.toLowerCase();

    // FIRST: Add Initial Donations (not tied to games) - check by ID or email
    const registrationFees = expenses.filter(e =>
        (e.isRegistrationFee || e.isInitialDonation) && // Check both flags
        e.approvalStatus === 'approved' && // Only count approved fees
        (
            (e.splitAmong && e.splitAmong.some(id => playerIds.includes(id))) ||
            (e.playerEmail && e.playerEmail.toLowerCase() === playerEmail)
        )
    );

    registrationFees.forEach(fee => {
        // Use amountPerPlayer if available (for bulk donations), otherwise amount
        const amount = fee.amountPerPlayer !== undefined ? parseFloat(fee.amountPerPlayer) : parseFloat(fee.amount);

        totalOwed += amount;
        expenseBreakdown.push({
            gameId: null,
            gameName: 'Initial Donation',
            gameDate: player?.registrationCompletedAt || fee.createdAt,
            expenseType: 'Initial Donation',
            expenseDate: fee.createdAt,
            totalAmount: amount, // Use the per-player amount
            playerShare: amount,
            squadSize: 1,
            isDividable: false,
            isSuperSub: false,
            isRegistrationFee: true
        });
    });

    // SECOND: Find games where player was in playing11
    let playerGames = games.filter(game =>
        game.playing11 && game.playing11.some(id => playerIds.includes(id))
    );

    // Filter by tournament if selected
    if (selectedTournament !== 'all') {
        playerGames = playerGames.filter(game => game.tournamentId === selectedTournament);
    }

    // Calculate expenses for those games
    playerGames.forEach(game => {
        const gameExpenses = expenses.filter(e => e.gameId === game.id);

        // Check if player is a super sub in this game
        const isSuperSub = game.superSubs && game.superSubs.some(id => playerIds.includes(id));

        gameExpenses.forEach(expense => {
            let playerShare = 0;

            // Check if expense has super sub support
            if (expense.isDividable && isSuperSub && expense.perSuperSubCost) {
                // Player is a super sub - use super sub cost
                playerShare = expense.perSuperSubCost;
            } else if (expense.isDividable && !isSuperSub && expense.perPlayerCost) {
                // Player is regular - use regular player cost
                playerShare = expense.perPlayerCost;
            } else if (expense.isDividable && expense.squadSize) {
                // Fallback for old expenses without super sub support
                playerShare = expense.amount / expense.squadSize;
            } else if (expense.amountPerPlayer) {
                // Old format
                playerShare = expense.amountPerPlayer;
            } else {
                // Oldest fallback: divide by squad size or 11
                const squadSize = game.playing11?.length || 11;
                playerShare = expense.amount / squadSize;
            }

            totalOwed += playerShare;

            expenseBreakdown.push({
                gameId: game.id,
                gameName: game.name,
                gameDate: game.date,
                expenseType: expense.type || expense.description,
                expenseDate: expense.date,
                totalAmount: expense.amount,
                playerShare: playerShare,
                squadSize: expense.squadSize || game.playing11?.length || 11,
                isDividable: expense.isDividable || false,
                isSuperSub: isSuperSub,
                isRegistrationFee: false
            });
        });
    });

    // SINGLE SOURCE OF TRUTH: walletTransactions with type='payment' or 'waiver'
    // Negative amounts = money received from player OR waived amount
    const playerWalletTransactions = walletTransactions.filter(t =>
        (t.type === 'payment' || t.type === 'waiver') &&
        t.status === 'approved' &&
        t.amount < 0 &&
        !t.isDonation && // Exclude donation payments from balance calculation
        (
            playerIds.includes(t.userId) ||
            (playerEmail && t.playerEmail?.toLowerCase() === playerEmail)
        )
    );

    // Calculate Paid and Waived separately
    totalPaid = playerWalletTransactions
        .filter(t => t.type === 'payment')
        .reduce((sum, t) => sum + Math.abs(t.amount), 0);

    const totalWaived = playerWalletTransactions
        .filter(t => t.type === 'waiver')
        .reduce((sum, t) => sum + Math.abs(t.amount), 0);

    return {
        totalOwed,
        totalPaid,
        totalWaived,
        balance: Math.max(0, totalOwed - totalPaid - totalWaived),
        gamesPlayed: playerGames.length,
        expenseBreakdown
    };
};

// Helper to calculate total receivables for the entire club
// MATCHES LOGIC IN PaymentManagement.jsx getSummaryStats()
export const calculateTotalReceivables = (players, expenses, games, walletTransactions) => {
    let globalTotalOwed = 0;
    let globalTotalPaid = 0;
    let globalTotalWaived = 0;

    players.forEach(player => {
        // Calculate stats for each player
        const stats = calculatePlayerOwed(player.id, players, expenses, games, walletTransactions);

        // Sum up raw totals (allowing overpayments to exist in the paid bucket)
        globalTotalOwed += stats.totalOwed;
        globalTotalPaid += stats.totalPaid;
        globalTotalWaived += stats.totalWaived;
    });

    // Calculate Net Receivables (Total Owed - Total Paid - Total Waived)
    // This allows one player's overpayment to reduce the total receivables, 
    // which aligns with how PaymentManagement calculates "Total Pending".
    return Math.max(0, globalTotalOwed - globalTotalPaid - globalTotalWaived);
};
